import type { CSSProperties } from 'react';

import {
  correctImageBreakpoints2023,
  type ImageBreakpointKeys,
  imageBreakpointKeys,
  largestPageWidth,
} from '../../config/breakpoints';
import type { SanityImageType } from '../../data/sanity/types';
import { getImageURL, isSemboTravelImageCdn } from '../../dtos/own/api-helpers';
import { SanityUrlService } from '../../services/SanityService/SanityService';
import type {
  ContentImageOptions,
  ImageOptions,
  ResponsiveImageResult,
  ViewportSize,
  ViewportsSizes,
} from './SemboImage.types';
import { getImageSizes } from './SemboImage.utils';

const getImageWithCustomWidth = (
  image: SanityImageType,
  width: number,
  imageOptions?: ImageOptions
) => {
  let transformedImage = SanityUrlService.urlFor(image.src)
    .fit('crop')
    .auto('format')
    .focalPoint(image.focalPointX ?? 0.5, image.focalPointY ?? 0.5)
    .width(width);

  if (imageOptions?.quality) {
    transformedImage = transformedImage.quality(imageOptions.quality);
  }

  if (imageOptions?.aspectRatio) {
    const [widthRatio, heightRatio] = imageOptions.aspectRatio.split(':');
    if (widthRatio && heightRatio) {
      const height = Math.ceil(
        (width / parseInt(widthRatio)) * parseInt(heightRatio)
      );
      transformedImage = transformedImage.height(height);

      return transformedImage.toString();
    }
  }

  if (imageOptions?.square) {
    transformedImage = transformedImage.height(width);
  }

  if (imageOptions?.height) {
    transformedImage = transformedImage.height(imageOptions.height);
  }

  if (imageOptions?.minHeight) {
    transformedImage = transformedImage.minHeight(imageOptions.minHeight);
  }

  return transformedImage.toString();
};

export function getSizeKeys(
  sizeElements: Partial<ViewportsSizes>
): ImageBreakpointKeys[] {
  return imageBreakpointKeys.filter(
    (key) =>
      sizeElements[key] || correctImageBreakpoints2023[key] <= largestPageWidth
  );
}

export function fillMissingBreakpoints(
  sizes: Partial<ViewportsSizes>
): Partial<ViewportsSizes> {
  const filledSizes: Partial<ViewportsSizes> = {};
  let lastSize: ViewportSize = '100vw';

  for (const key of imageBreakpointKeys) {
    if (
      sizes[key] === undefined &&
      correctImageBreakpoints2023[key] > largestPageWidth
    ) {
      // Don't add this image unless it was explicitly defined
    } else if (sizes[key] === undefined) {
      filledSizes[key] = lastSize;
    } else {
      lastSize = sizes[key] as ViewportSize;
      filledSizes[key] = sizes[key];
    }
  }

  return filledSizes;
}

export const useResponsiveSanityImage = (
  sizeElements: Partial<ViewportsSizes>,
  sanityImage?: SanityImageType | null,
  imageOptions?: ImageOptions
): Partial<ResponsiveImageResult> => {
  if (sanityImage?.src === undefined || sanityImage?.src === null) {
    return {};
  }

  const filledSizes = fillMissingBreakpoints(sizeElements);
  const sizeKeys = getSizeKeys(sizeElements);

  // Create srcSet based on the provided size elements and the original image
  const srcSet = sizeKeys
    .map((key) => {
      return `${getImageWithCustomWidth(
        sanityImage,
        correctImageBreakpoints2023[key] + 1,
        imageOptions
      )} ${correctImageBreakpoints2023[key]}w`;
    })
    .join(', ');

  const sizes = getImageSizes(sizeKeys, filledSizes);

  const src = getImageWithCustomWidth(
    sanityImage,
    largestPageWidth + 1,
    imageOptions
  );

  const style: CSSProperties =
    typeof sanityImage?.focalPointX === 'number' ||
    typeof sanityImage?.focalPointY === 'number'
      ? {
          objectPosition: `${(sanityImage.focalPointX ?? 0.5) * 100}% ${
            (sanityImage.focalPointY ?? 0.5) * 100
          }%`,
        }
      : {};

  return {
    srcSet,
    sizes,
    src,
    style,
    alt: sanityImage.description ?? undefined,
  };
};

const getImageUrlWithCustomWidth = (
  image: string,
  width: number,
  imageService?: string,
  imageOptions?: ImageOptions
) => {
  if (imageOptions?.aspectRatio) {
    const [widthRatio, heightRatio] = imageOptions.aspectRatio.split(':');
    if (widthRatio && heightRatio) {
      const height = Math.ceil(
        (width / parseInt(widthRatio)) * parseInt(heightRatio)
      );
      return getImageURL(image, imageService, width, height);
    }
  }

  if (imageOptions?.square) {
    return getImageURL(image, imageService, width, width);
  }
  return getImageURL(image, imageService, width, imageOptions?.height);
};

export const useResponsiveContentImage = (
  sizeElements: Partial<ViewportsSizes>,
  contentImage?: string,
  imageService?: string,
  imageOptions?: ContentImageOptions
): Partial<ResponsiveImageResult> => {
  if (!contentImage) {
    return {};
  }

  if (!isSemboTravelImageCdn(contentImage)) {
    // we can't scale the image if it's not from our cdn
    return { src: contentImage };
  }

  const filledSizes = fillMissingBreakpoints(sizeElements);
  const sizeKeys = getSizeKeys(sizeElements);

  const srcSet = sizeKeys
    .map((key) => {
      return `${getImageUrlWithCustomWidth(
        contentImage,
        correctImageBreakpoints2023[key] + 1,
        imageService,
        imageOptions
      )} ${correctImageBreakpoints2023[key]}w`;
    })
    .join(', ');
  const sizes = getImageSizes(sizeKeys, filledSizes);

  const src = getImageUrlWithCustomWidth(
    contentImage,
    largestPageWidth + 1,
    imageService,
    imageOptions
  );

  return { srcSet, sizes, src };
};
